import React, { useState } from "react";
import {
  Avatar,
  Button,
  Image,
  Input,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";

interface PhotoInputProps {
  value?: string;
  required?: boolean;
  isAvatar?: boolean;
  maxSize?: number; // Size in MB
  onFileSizeError?: (fileName: string, size: number) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
}

export const PhotoInput = React.forwardRef<HTMLInputElement, PhotoInputProps>(
  ({ 
    value, 
    required, 
    isAvatar, 
    maxSize = 5, // 5MB default
    onFileSizeError,
    onChange,
    accept = "image/png,image/jpg,image/jpeg",
    ...props 
  }, ref) => {
    const [image, setImage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [id] = useState((Math.random() + 1).toString(36).substring(7));

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const validateFile = (file: File): boolean => {
      const maxSizeInBytes = maxSize * 1024 * 1024; // Convert MB to bytes
      
      if (file.size > maxSizeInBytes) {
        const errorMessage = `Arquivo muito grande: ${formatFileSize(file.size)}. Máximo permitido: ${maxSize}MB`;
        setError(errorMessage);
        if (onFileSizeError) {
          onFileSizeError(file.name, file.size);
        }
        return false;
      }
      
      setError(null);
      return true;
    };

    const readURL = (input: HTMLInputElement) => {
      if (input.files && input.files[0]) {
        const file = input.files[0];
        
        if (!validateFile(file)) {
          // Clear the input if file is invalid
          input.value = '';
          setImage(null);
          return false;
        }

        const reader = new FileReader();
        reader.onload = function (e) {
          setImage(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        return true;
      }
      return false;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const isValid = readURL(e.target as HTMLInputElement);
      
      // Only call onChange if file is valid
      if (isValid && onChange) {
        onChange(e);
      } else if (!isValid) {
        // Clear the input value to prevent invalid file from being submitted
        e.target.value = '';
      }
    };

    const handleButtonClick = () => {
      setError(null); // Clear error when opening file dialog
      document.getElementById(id)?.click();
    };

    const clearImage = () => {
      setImage(null);
      setError(null);
      const input = document.getElementById(id) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    };

    return (
      <VStack width="100%" align="flex-start" spacing={3}>
        {/* Image Preview */}
        {(image || value) && (
          <VStack spacing={2} width="100%">
            {isAvatar ? (
              <Avatar src={image || value} size="xl" />
            ) : (
              <Image
                minH="120px"
                maxH="250px"
                width="100%"
                objectFit="cover"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                src={image || value}
                alt="Preview da imagem"
              />
            )}
            <Button
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={clearImage}
            >
              Remover imagem
            </Button>
          </VStack>
        )}

        {/* Error Alert */}
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription fontSize="sm">{error}</AlertDescription>
          </Alert>
        )}

        {/* Upload Button */}
        <Button
          colorScheme={value || image ? "blue" : "green"}
          variant={value || image ? "outline" : "solid"}
          onClick={handleButtonClick}
          size="sm"
        >
          {value || image ? "Trocar Imagem" : "Adicionar Imagem"}
        </Button>

        {/* File Size Info */}
        <Text fontSize="xs" color="gray.500">
          Tamanho máximo: {maxSize}MB • Formatos: PNG, JPG, JPEG
        </Text>

        {/* Hidden File Input */}
        <Input
          display="none"
          ref={ref}
          id={id}
          type="file"
          required={required && !value && !image}
          accept={accept}
          {...props}
          onChange={handleFileChange}
        />
      </VStack>
    );
  }
);
